import React, { useMemo, useState } from 'react'
import Title from 'antd/es/typography/Title'
import '../../assignment.scss'
import { Alert, Button, Card, Checkbox, Empty, Flex, Space, Statistic } from 'antd'
import { actions } from '../../../../store/reducers/assignment.reducer'
import { connect, useDispatch, useSelector } from 'react-redux'
import { ArrowsAltOutlined, EditOutlined, EyeOutlined, ShrinkOutlined } from '@ant-design/icons'
import EditClusterModal from './EditClusterModal'
import { ASSIGNMENT_TYPES } from '../../../../constants/assignment-types'
import { CLUSTER_X_ID, CLUSTER_Y_ID } from '../../../../constants/cluster-ids'
import _ from 'lodash'
import { useThemeColors } from '../../../../hooks/useThemeColors'
import { useParams } from 'react-router'
import Access, { useAssignmentAccess } from '../access/Access'
import { LANGUAGES } from '../../../../constants/languages'
import patternSelectors from '../../../../store/selectors/pattern.selectors'
import { useDeleteGroupMutation } from '../../../../store/apis/group.api'
import { groupFilesBySetOfPatterns } from '../../../../utils/cluster.helper'
import assignmentSelectors from '../../../../store/selectors/assignment.selectors'
import submissionSelectors from '../../../../store/selectors/submission.selectors'
import { submissionActions } from '../../../../store/slices/submission.slice'
import FileCardContainer from '../file-card/FileCardContainer'
import { ContentRules } from '../access/access-rules/assignment-rules'

const Cluster = ({
    cluster,
    clusters: notFilteredClusters,
    collapsedClusterIds,
    layers,
    unitTests,
    unitTestResults,
    isWaitingForUnitTestResults,
    queryComparisonMode,
    switchClusterCollapse,
}) => {
    const colors = useThemeColors()
    const finetunedPatternExists = useSelector(patternSelectors.finetunedPatternExists)
    const language = useSelector(assignmentSelectors.language)
    const assignmentType = useSelector(assignmentSelectors.type)
    const someFileIsSelected = useSelector(submissionSelectors.someIsSelected(cluster.files.map(file => file.id)))
    const allFilesAreSelected = useSelector(submissionSelectors.allAreSelected(cluster.files.map(file => file.id)))
    const dispatch = useDispatch()
    const { token, layerId } = useParams()

    const [deleteGroup, deleteGroupResult] = useDeleteGroupMutation()

    const [showEditClusterModal, setShowEditClusterModal] = useState(false)

    const isClusterCollapsed = useMemo(
        () => collapsedClusterIds.includes(cluster.cluster_id),
        [collapsedClusterIds, cluster.cluster_id],
    )
    const filesGroupedBySetOfPatterns = useMemo(() => groupFilesBySetOfPatterns(cluster.files), [cluster.files])

    const fileCards = useMemo(() => {
        let fileCards = []
        if (isClusterCollapsed) {
            filesGroupedBySetOfPatterns.forEach(filesGroup =>
                fileCards.push(
                    <FileCardContainer
                        key={filesGroup[0].id}
                        file={filesGroup[0]}
                        badgeProps={{
                            count: filesGroup.length,
                            title: `${filesGroup.length} files of such pattern set in this group`,
                            overflowCount: 999,
                            color: colors.primary,
                        }}
                    />,
                ),
            )
        } else {
            fileCards = cluster.files.map(file => <FileCardContainer key={file.id} file={file} />)
        }

        return (
            <Space size={isClusterCollapsed ? 16 : 8} wrap>
                {fileCards}
            </Space>
        )
    }, [cluster.files, isClusterCollapsed, filesGroupedBySetOfPatterns])

    const handleCheckAllChange = event =>
        event.target.checked
            ? dispatch(submissionActions.addManySelectedSubmissionIds(cluster.files.map(file => file.id)))
            : dispatch(submissionActions.removeManySelectedSubmissionIds(cluster.files.map(file => file.id)))

    const handleEditClusterClick = () => setShowEditClusterModal(true)

    const handleDeleteClusterClick = () => {
        deleteGroup({ token, layerId, groupId: cluster.cluster_id })
    }

    const notFilteredCluster = notFilteredClusters.find(nfc => nfc.cluster_id === cluster.cluster_id)

    let clusterContent
    if (
        !notFilteredCluster?.files?.length &&
        ![CLUSTER_X_ID, CLUSTER_Y_ID].includes(cluster.cluster_id) && //we shouldn't allow to delete Group X (id 0) and Group Y (id 999)
        assignmentType !== ASSIGNMENT_TYPES.MODEL_BOUND_ASSIGNMENT.value &&
        !finetunedPatternExists &&
        !queryComparisonMode
    ) {
        clusterContent = (
            <Alert
                message='This group seems to have no files. Do you want to delete it?'
                showIcon
                type='info'
                action={
                    <Space>
                        <Button
                            size='small'
                            danger
                            onClick={handleDeleteClusterClick}
                            loading={deleteGroupResult.isLoading}
                        >
                            Delete
                        </Button>
                    </Space>
                }
            />
        )
    } else if (notFilteredCluster?.files?.length && cluster?.files?.length) {
        clusterContent = fileCards
    } else {
        clusterContent = <Empty description='There are no files' image={Empty.PRESENTED_IMAGE_SIMPLE} />
    }

    const { hasAccess: hasEditingPermission } = useAssignmentAccess(ContentRules.EDIT_NAMINGS, token)

    const amountOfHidebleFiles = cluster.files.length - filesGroupedBySetOfPatterns.size
    const shouldShowCollapseButton = cluster.files.length > filesGroupedBySetOfPatterns.size

    const cardTitle = (
        <div className='d-flex-start cluster__title-container'>
            <Title className='cluster__title' aria-label={`${cluster.cluster_name} group`} level={2} ellipsis={{ tooltip: false }} title={cluster.cluster_name}>
                {cluster.cluster_name}
            </Title>
            <Button
                onClick={handleEditClusterClick}
                type='text'
                shape='circle'
                aria-label={hasEditingPermission ? 'edit group' : 'view group'}
            >
                <Access
                    rules={ContentRules.EDIT_NAMINGS}
                    fallback={<EyeOutlined />}
                >
                    <EditOutlined />
                </Access>
            </Button>
            {shouldShowCollapseButton && (
                <Button
                    type='text'
                    shape='circle'
                    icon={isClusterCollapsed ? <ArrowsAltOutlined /> : <ShrinkOutlined />}
                    onClick={() => switchClusterCollapse(cluster.cluster_id)}
                    title={
                        isClusterCollapsed
                            ? `Show ${amountOfHidebleFiles} more submissions of that group`
                            : `Hide ${amountOfHidebleFiles} submissions of that group`
                    }
                />
            )}
            <Checkbox
                checked={cluster.files.length && allFilesAreSelected}
                indeterminate={someFileIsSelected && !allFilesAreSelected}
                onChange={handleCheckAllChange}
                aria-label='select group'
            />
        </div>
    )

    const currentLayerWeight =
        layers.find(layer => layer.layer_id === parseInt(layerId))?.layer_weight ?? 0
    const unitTestsLayerWeight =
        layers.find(layer => layer.layer_name === 'UnitTests999')?.layer_weight ?? 0
    const currentClusterGrade = parseInt(cluster.grade)
    const currentClusterCalculatedGrade = Math.round(
        ((isNaN(currentClusterGrade) ? 0 : currentClusterGrade) * currentLayerWeight) / 100,
    )

    // Ignore missing UT results: compute per-file percent based only on available results, then average
    const perFilePercents = cluster.files.map(file => {
        let availableCount = 0
        let passedCount = 0
        unitTests.forEach(unitTest => {
            const result = unitTestResults.find(
                unitTestResult => unitTestResult.file_id === file.id && unitTestResult.ut_id === unitTest.id,
            )
            if (result !== undefined) {
                availableCount += 1
                if (result.ut_passed) passedCount += 1
            }
        })
        return availableCount ? (100 * passedCount) / availableCount : null
    })

    const validFilePercents = perFilePercents.filter(v => v !== null)
    const passedUnitTestsPercent = validFilePercents.length ? _.mean(validFilePercents) : 0
    const unitTestsGrade = (passedUnitTestsPercent * unitTestsLayerWeight) / 100
    const unitTestsGradeRounded = Math.round(unitTestsGrade * 10) / 10

    const totalSubmissionsCount = notFilteredClusters.reduce((acc, cluster) => acc + cluster.files.length, 0)
    const submissionsPercentOfTotal = totalSubmissionsCount
        ? Math.round((cluster.files.length * 100) / totalSubmissionsCount)
        : 0
    
    const cardExtra = (
        <Flex gap={32} wrap='wrap'>
            {!unitTests.length ||
            isNaN(unitTestsGrade) ||
            isWaitingForUnitTestResults ||
            language === LANGUAGES.TEXT.value ? null : (
                <Statistic title='Avg. UT grade' value={unitTestsGradeRounded} suffix={`/ ${unitTestsLayerWeight}`} />
            )}
            <Statistic title='Grade' value={currentClusterCalculatedGrade ?? 0} suffix={`/ ${currentLayerWeight}`} />
            <Statistic title='Submissions' value={`${cluster.files.length} (${submissionsPercentOfTotal}%)`} />
        </Flex>
    )

    return (
        <Card bordered={false} className='cluster-container' title={cardTitle} extra={cardExtra}>
            {showEditClusterModal && (
                <EditClusterModal
                    id={cluster.cluster_id}
                    name={cluster.cluster_name}
                    feedback={cluster.feedback}
                    grade={cluster.grade}
                    conceptualLabels={cluster.conceptual_labels}
                    warningLevel={cluster.warning_level}
                    showEditClusterModal={showEditClusterModal}
                    setShowEditClusterModal={setShowEditClusterModal}
                />
            )}
            <div>{clusterContent}</div>
        </Card>
    )
}

const mapState = ({ assignment }) => {
    return {
        clusters: assignment.clusters,
        collapsedClusterIds: assignment.collapsedClusterIds,
        layers: assignment.layers,
        unitTests: assignment.unitTests,
        unitTestResults: assignment.unitTestResults,
        isWaitingForUnitTestResults: assignment.isWaitingForUnitTestResults,
        queryComparisonMode: assignment.queryComparisonMode,
    }
}

const mapDispatch = dispatch => {
    return {
        switchClusterCollapse(value) {
            dispatch(actions.switchClusterCollapse(value))
        },
    }
}

export default connect(mapState, mapDispatch)(React.memo(Cluster))
